import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "b94_pwa_install_dismissed_until";
const HIDE_DAYS = 7;

/**
 * PWA install prompt. Shows a discreet banner when the browser fires
 * `beforeinstallprompt`. Users can dismiss for 7 days.
 * On iOS Safari the event is never fired — we skip silently.
 */
export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() < dismissedUntil) return;
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => {
      setVisible(false);
      setDeferred(null);
    });
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (!visible || !deferred) return null;

  const install = async () => {
    deferred.prompt();
    try {
      const { outcome } = await deferred.userChoice;
      if (outcome === "dismissed") {
        localStorage.setItem(DISMISS_KEY, String(Date.now() + HIDE_DAYS * 24 * 3600 * 1000));
      }
    } catch {
      // ignore
    }
    setVisible(false);
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + HIDE_DAYS * 24 * 3600 * 1000));
    setVisible(false);
  };

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-80 z-50 bg-zinc-900 border border-yellow-400/40 rounded-xl shadow-2xl p-4"
    >
      <button
        onClick={dismiss}
        data-testid="pwa-dismiss-btn"
        aria-label="Cerrar"
        className="absolute top-2 right-2 text-zinc-500 hover:text-white p-1"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="w-10 h-10 rounded-lg bg-yellow-400 flex items-center justify-center shrink-0">
          <Download className="w-5 h-5 text-black" strokeWidth={2.5} />
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold text-sm text-white">Instala Portal B94</div>
          <div className="text-xs text-zinc-400 mt-0.5">
            Añádelo a tu pantalla de inicio y accede como una app nativa.
          </div>
          <button
            onClick={install}
            data-testid="pwa-install-btn"
            className="mt-3 w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-sm py-2 rounded-md transition-colors"
          >
            Instalar
          </button>
        </div>
      </div>
    </div>
  );
}
